import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";

export default function RegistrationSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking"); // checking, success, failed
  const [message, setMessage] = useState("Verifying your payment...");
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      setMessage("No payment session found");
      return;
    }

    pollPaymentStatus(sessionId, 0);
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, attempts) => {
    const maxAttempts = 10;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setStatus("failed");
      setMessage("Payment verification timed out. Please contact support.");
      return;
    }

    try {
      const response = await apiClient.get(`/subscriptions/checkout-status/${sessionId}`);
      
      if (response.data.status === "paid") {
        setStatus("success");
        setMessage("Payment successful! Setting up your account...");
        
        // Store auth data
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.user));
        localStorage.setItem("company", JSON.stringify(response.data.company));
        
        // Redirect to welcome page after 2 seconds
        setTimeout(() => {
          navigate("/welcome");
        }, 2000);
        return;
      } else if (response.data.status === "expired") {
        setStatus("failed");
        setMessage("Payment session expired. Please try again.");
        return;
      }

      // If payment is still pending, continue polling
      setMessage(`Payment is being processed... (${attempts + 1}/${maxAttempts})`);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setStatus("failed");
      setMessage("Error verifying payment. Please contact support.");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-8">
      <Card className="max-w-md w-full">
        <CardHeader>
          <h1 className="sr-only">SmartReqs Registration - Payment Status</h1>
          <CardTitle className="text-center text-2xl">
            {status === "checking" && "Processing Payment"}
            {status === "success" && "Payment Successful!"}
            {status === "failed" && "Payment Failed"}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {status === "checking" && (
            <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto" />
          )}
          {status === "success" && (
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto" />
          )}
          {status === "failed" && (
            <XCircle className="w-16 h-16 text-red-600 mx-auto" />
          )}
          <p className="text-slate-700">{message}</p>
          {status === "failed" && (
            <button
              onClick={() => navigate("/pricing")}
              className="mt-4 text-blue-600 hover:underline"
            >
              Try Again
            </button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
